import { TEXTURE_SIZE, renderProceduralTexture } from './procedural';

const PADDING = 2;

export interface AtlasSlot {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

export interface SourceTexture {
  data: Uint8ClampedArray;
  size: number;
}

export interface TextureAtlas {
  data: Uint8ClampedArray;
  width: number;
  height: number;
  tile: number;
  slots: Map<string, AtlasSlot>;
  /** リソースパック由来のタイル数。0なら全て自前生成。 */
  sourced: number;
}

function sampleSource(source: SourceTexture, x: number, y: number, tile: number, offset: number) {
  const sourceX = Math.min(source.size - 1, Math.floor((x * source.size) / tile));
  const sourceY = Math.min(source.size - 1, Math.floor((y * source.size) / tile));
  return (sourceY * source.size + sourceX) * 4 + offset;
}

function writeTile(
  atlas: Uint8ClampedArray,
  atlasWidth: number,
  originX: number,
  originY: number,
  tile: number,
  source: SourceTexture,
) {
  for (let y = -PADDING; y < tile + PADDING; y += 1) {
    const clampedY = y < 0 ? 0 : y >= tile ? tile - 1 : y;
    for (let x = -PADDING; x < tile + PADDING; x += 1) {
      const clampedX = x < 0 ? 0 : x >= tile ? tile - 1 : x;
      // アトラスは行0がv=0（下端）なので、上下を反転して書き込む。
      const target = ((originY + tile - 1 - y) * atlasWidth + originX + x) * 4;
      for (let channel = 0; channel < 4; channel += 1) {
        atlas[target + channel] = source.data[sampleSource(source, clampedX, clampedY, tile, channel)];
      }
    }
  }
}

/**
 * ブロックテクスチャをひとつのアトラスへ詰める。`sources`で渡した名前は
 * リソースパックのピクセルを使い、それ以外は自前生成のテクスチャを使う。
 */
export function buildAtlas(
  names: Iterable<string>,
  options: { tile?: number; sources?: Map<string, SourceTexture> } = {},
): TextureAtlas {
  const unique = [...new Set(names)].sort();
  if (unique.length === 0) unique.push('stone');

  const tile = options.tile ?? TEXTURE_SIZE;
  const cell = tile + PADDING * 2;
  const columns = Math.ceil(Math.sqrt(unique.length));
  const rows = Math.ceil(unique.length / columns);
  const width = columns * cell;
  const height = rows * cell;
  const data = new Uint8ClampedArray(width * height * 4);
  const slots = new Map<string, AtlasSlot>();
  let sourced = 0;

  unique.forEach((name, index) => {
    const column = index % columns;
    const row = Math.floor(index / columns);
    const originX = column * cell + PADDING;
    const originY = row * cell + PADDING;

    const packed = options.sources?.get(name);
    if (packed) sourced += 1;
    const source: SourceTexture = packed ?? { data: renderProceduralTexture(name), size: TEXTURE_SIZE };
    writeTile(data, width, originX, originY, tile, source);

    slots.set(name, {
      u0: originX / width,
      v0: originY / height,
      u1: (originX + tile) / width,
      v1: (originY + tile) / height,
    });
  });

  return { data, width, height, tile, slots, sourced };
}
